import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAutoSave — debounced auto-save with status tracking + beforeunload guard.
 *
 * Compares the live value against a baseline (last server-acknowledged snapshot),
 * fires a save 1.5s after the last change, and reports status:
 *  - "idle"      no pending changes
 *  - "dirty"     edits pending, debounce hasn't fired yet
 *  - "saving"    PUT in flight
 *  - "saved"     successful save; lastSavedAt is set
 *  - "error"     save failed; lastError holds the message
 *
 * The hook also returns a manual `flush()` to commit immediately (e.g. wired to
 * the existing "save piece" button) and a `markBaseline(snapshot)` to reset
 * dirtiness after a parent-driven reload.
 *
 * The beforeunload guard activates only while status === "dirty" || "saving".
 *
 * Caller contract:
 *  - `value` should be the full editable shape (the page's `item` state).
 *  - `save(diff)` should `PUT` and return a Promise<savedSnapshot>.
 *  - `equal(a,b)` defaults to JSON.stringify equality — pass a custom comparator
 *    when the shape contains keys irrelevant for the diff (e.g. derived totals).
 */
export function useAutoSave({ value, save, debounceMs = 1500, equal }) {
  const [status, setStatus] = useState("idle");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastError, setLastError] = useState(null);

  // Refs so the timer callback always reads the latest closure.
  const baselineRef = useRef(value);
  const latestRef = useRef(value);
  const timerRef = useRef(null);
  const inflightRef = useRef(false);
  // Track whether the next baseline change should not produce a "dirty" tick
  // (e.g. the very first mount, or a parent-driven reload via markBaseline).
  const skipDirtyRef = useRef(true);

  const isEqual = useCallback((a, b) => {
    if (equal) return equal(a, b);
    try { return JSON.stringify(a) === JSON.stringify(b); } catch { return a === b; }
  }, [equal]);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const candidate = latestRef.current;
    if (isEqual(candidate, baselineRef.current)) return;
    if (inflightRef.current) {
      // Another save already running — let the trailing change trigger a follow-up.
      return;
    }
    inflightRef.current = true;
    setStatus("saving");
    try {
      const savedSnapshot = await save(candidate);
      baselineRef.current = savedSnapshot ?? candidate;
      setLastSavedAt(Date.now());
      setLastError(null);
      // If user kept typing during save, schedule another round.
      if (!isEqual(latestRef.current, baselineRef.current)) {
        setStatus("dirty");
        timerRef.current = setTimeout(() => flush(), debounceMs);
      } else {
        setStatus("saved");
      }
    } catch (e) {
      setLastError(e?.response?.data?.detail || e?.message || "save failed");
      setStatus("error");
    } finally {
      inflightRef.current = false;
    }
  }, [save, isEqual, debounceMs]);

  // Re-arm the timer whenever the value changes.
  useEffect(() => {
    latestRef.current = value;
    // Don't start tracking dirtiness until the parent has finished its initial fetch.
    // Otherwise the null → fetched-object transition is incorrectly flagged as a user edit.
    if (value === null || value === undefined) return;
    if (skipDirtyRef.current) {
      // First defined value — adopt it as the baseline silently.
      baselineRef.current = value;
      skipDirtyRef.current = false;
      return;
    }
    if (isEqual(value, baselineRef.current)) {
      if (status === "dirty") setStatus("saved");
      return;
    }
    setStatus("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(), debounceMs);
    return () => {
      // do not clear here — we want the timer to outlive value changes within the
      // debounce window. flush() handles its own cleanup.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Cleanup on unmount — fire one last save if there's a pending change.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (!isEqual(latestRef.current, baselineRef.current)) {
        // Best-effort fire-and-forget on unmount; we can't await an async fn here.
        try {
          save(latestRef.current);
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn("useAutoSave: unmount-save failed", err);
          }
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // beforeunload guard — only when there's actual unsaved work.
  useEffect(() => {
    const handler = (e) => {
      if (status === "dirty" || status === "saving") {
        e.preventDefault();
        e.returnValue = ""; // required for the chrome confirmation dialog
        return "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [status]);

  // Reset to a fresh baseline (e.g. after a parent-driven reload).
  const markBaseline = useCallback((snapshot) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    baselineRef.current = snapshot;
    latestRef.current = snapshot;
    skipDirtyRef.current = true;
    setStatus("idle");
  }, []);

  return { status, lastSavedAt, lastError, flush, markBaseline };
}
